// @ts-check

import z from "zod";
import * as Sentry from "@sentry/react";
import { WebSearchArgsSchema, WebSearchResultSchema, WebSearchToolUI } from "./web-search";
import { GenerateReportArgsSchema, GenerateReportResultSchema, GenerateReportUI } from "./generate-report";
import { AgentTaskArgsSchema, AgentTaskResultSchema, AgentTaskUI } from "./agent-task";
import {
  CreateCanvasArgsSchema, CreateCanvasResultSchema, CreateCanvasUI,
  EditDocumentArgsSchema, EditDocumentResultSchema, EditDocumentUI,
  WriteDocumentArgsSchema, WriteDocumentResultSchema, WriteDocumentUI,
} from "./canvas";
import ToolCall from "@/components/tool-call";
import { parse as parseDirtyJSON } from "dirty-json"

/** FNV-1a 32-bit hash mixed with string length to reduce collision risk */
function hashStr(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(36) + '-' + str.length
}

const parseCache = new Map()
const MAX_PARSE_CACHE_SIZE = 500

/** Drop-in for parseDirtyJSON with string-keyed result cache */
function cachedParseDirtyJSON(str) {
  const key = hashStr(str)
  if (parseCache.has(key)) return parseCache.get(key)
  if (parseCache.size >= MAX_PARSE_CACHE_SIZE) {
    parseCache.delete(parseCache.keys().next().value)
  }
  const result = parseDirtyJSON(str)
  parseCache.set(key, result)
  return result
}

import { Wrench, Copy, Check } from "lucide-react";
import JsonTreeView from "@/components/ui/json-tree-view";
import { Button } from "@/components/ui/button";
import { useState } from "react";

/**
 * @template {z.ZodType} ArgsSchema
 * @template {z.ZodType} ResultSchema
 * @typedef {Object} ToolComponentProps
 * @property {string} name
 * @property {z.infer<ArgsSchema>} args
 * @property {z.infer<ResultSchema> | Error| null} result
 * @property {typeof ToolCall["State"][keyof typeof ToolCall["State"]]} state
 */

/**
 * @template {z.ZodType} ArgsSchema
 * @template {z.ZodType} ResultSchema
 * @typedef {Object} ToolDefinition
 * @property {string} name
 * @property {ArgsSchema} argsSchema
 * @property {ResultSchema} resultSchema
 * @property {React.FC<ToolComponentProps<ArgsSchema, ResultSchema>>} Component
 */

/** @satisfies {ToolDefinition<z.ZodType, z.ZodType>[]} */
const ToolDefinitions = [
  {
    name: "web_search",
    argsSchema: WebSearchArgsSchema,
    resultSchema: WebSearchResultSchema,
    Component: WebSearchToolUI,
  },
  {
    name: "generate_report",
    argsSchema: GenerateReportArgsSchema,
    resultSchema: GenerateReportResultSchema,
    Component: GenerateReportUI,
  },
  {
    name: "agent_task",
    argsSchema: AgentTaskArgsSchema,
    resultSchema: AgentTaskResultSchema,
    Component: AgentTaskUI,
  },
  {
    name: "create_canvas",
    argsSchema: CreateCanvasArgsSchema,
    resultSchema: CreateCanvasResultSchema,
    Component: CreateCanvasUI,
  },
  {
    name: "edit_document",
    argsSchema: EditDocumentArgsSchema,
    resultSchema: EditDocumentResultSchema,
    Component: EditDocumentUI,
  },
  {
    name: "write_document",
    argsSchema: WriteDocumentArgsSchema,
    resultSchema: WriteDocumentResultSchema,
    Component: WriteDocumentUI,
  },
]

/**
 * @param {ToolComponentProps<z.ZodString, z.ZodString>} props
 */
function DefaultToolUI({ name, args, result }) {
  const [copied, setCopied] = useState(false);

  const parsedResult = (() => {
    try {
      return getParsedToolParam(result, z.object({}).passthrough())
    } catch(error) {
      Sentry.captureException(error, {
        extra: {
          context: 'DefaultToolUI - getParsedToolParam',
          toolName: name,
          result,
          isResultError: result instanceof Error
        }
      });
      return error
    }
  })()

  const output = parsedResult instanceof Error
    ? parsedResult.message
    : parsedResult
  const prettifiedOutput = prettifyToolResult(output)

  const toolState = (() => {
    if(parsedResult instanceof Error) return ToolCall.State.ERROR
    if(parsedResult == null) return ToolCall.State.PENDING
    return ToolCall.State.SUCCESS
  })()

  const copyJsonToClipboard = async () => {
    if(toolState !== ToolCall.State.SUCCESS) return;

    const jsonString = JSON.stringify(prettifiedOutput, null, 2);
    await navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ToolCall state={toolState}>
      <ToolCall.Header>
        <Wrench size={16} className="shrink-0" />
        <ToolCall.Action>{prettifyToolName(name)}</ToolCall.Action>
        {/* <ToolCall.Input>{args}</ToolCall.Input> */}
      </ToolCall.Header>

      <ToolCall.Content>
        <ToolCall.Output>
          {toolState === ToolCall.State.SUCCESS && (
            <div className="relative">
              <div className="absolute top-0 right-0 z-10 flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyJsonToClipboard}
                  className="gap-1.5"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy JSON
                    </>
                  )}
                </Button>
              </div>
              <JsonTreeView
                data={prettifiedOutput}
                showCopyButtons={true}
                showDataTypes={false}
              />
            </div>
          )}
          {toolState === ToolCall.State.ERROR && (
            <span>{typeof output === "object" ? JSON.stringify(output): output}</span>
          )}
        </ToolCall.Output>
      </ToolCall.Content>
    </ToolCall>
  )
}

/** @param {string} name */
function prettifyToolName(name) {
  if(!name.includes("_")) return name
  return name.replace(/_/g, ' ').replace(/^\w/, l => l.toUpperCase())
}

/**
  * @param {*} result
  * @returns  {*}
  */
function prettifyToolResult(result) {
  const isArray = Array.isArray(result);
  const isObject = result != null && typeof result === 'object' && !Array.isArray(result);

  if(!isObject && !isArray) return result

  if(isArray) {
    return result.map(item => prettifyToolResult(item))
  }

  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [
      key.replace(/_/g, ' '),
      prettifyToolResult(value)
    ])
  )
}

/**
 * @template T
 * @param {string|null|Error} data 
 * @param {z.ZodType<T>} paramSchema 
 */
function getParsedToolParam(data, paramSchema) {
  if(data == null) return null
  // ! Currently a hack, should be improved
  if(typeof data === "string" && data.startsWith("1 validation error")) return new Error(data)
  if(data instanceof Error) return data

  let jsonData;
  try {
    jsonData = cachedParseDirtyJSON(data)
  } catch(error) {
    return new Error(data)
  }

  if(typeof jsonData === 'object' && jsonData !== null && "error" in jsonData) return new Error(jsonData.error)

  const parsed = paramSchema.safeParse(jsonData)
  if (!parsed.success) return new Error(parsed.error.message)
  return parsed.data
}

export {
  ToolDefinitions,
  DefaultToolUI,
  getParsedToolParam,
  cachedParseDirtyJSON,
}
